﻿export function createCollectionSlug(name: string): string {
    return name.trim().toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 120);
}

export function getCollectionSlug(search: string): string | null {
    const value = new URLSearchParams(search).get('collection');
    return value != null && /^[A-Za-z0-9](?:[A-Za-z0-9_-]*[A-Za-z0-9])?$/.test(value) ? value : null;
}

export function getCollectionEditorTargetItemsPerRow(usableWidth: number): number {
    const gapPixels = 10;
    const minimumThumbnailWidthPixels = 280;
    return Math.min(4, Math.max(1, Math.floor((usableWidth + gapPixels) / (minimumThumbnailWidthPixels + gapPixels))));
}

export function getCollectionEditorPartialRowHeight(
    preferredHeight: number,
    aspectRatios: readonly number[],
    usableWidth: number,
    gapPixels = 10
): number {
    const availableImageWidth = usableWidth - gapPixels * Math.max(0, aspectRatios.length - 1);
    const aspectRatioSum = aspectRatios.reduce((sum, aspectRatio) => sum + aspectRatio, 0);
    return Math.min(preferredHeight, availableImageWidth / aspectRatioSum);
}

export function isAcceptedImageFile(file: Pick<File, 'name' | 'type'>): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return file.type === 'image/jpeg' || file.type === 'image/tiff'
        || extension === 'jpg' || extension === 'jpeg' || extension === 'tif' || extension === 'tiff';
}

export type DuplicateUploadChoice = 'cancel' | 'update' | 'duplicate';

export type DuplicateUploadAction = 'replace' | 'duplicate';

export type DuplicateUploadApproval = {
    action: DuplicateUploadAction;
    replaceImageKey: string | null;
};

/**
 * Maps a duplicate-image dialog choice to the upload work item for that file. Cancel returns null so the
 * file is skipped, update targets the existing image key, and duplicate creates a brand-new image.
 */
export function resolveDuplicateUploadChoice(
    choice: DuplicateUploadChoice,
    existingImageKey: string
): DuplicateUploadApproval | null {
    if (choice === 'cancel') return null;
    return choice === 'update'
        ? { action: 'replace', replaceImageKey: existingImageKey }
        : { action: 'duplicate', replaceImageKey: null };
}
